import React, { useEffect, useMemo, useState } from 'react';
import { marginReductionPercentToDisplay, marginDisplayPercentToReduction } from '../utils/clientPricingMatrix';

const normalizeNumericId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? String(id) : '';
};

const normalizeCode = (value) => String(value || '').trim();

const buildOptions = (items, getValue, getLabel) => {
  const options = [];
  const seen = new Set();

  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item || item.isActive === false || item.isDeleted === true) return;
    const value = getValue(item);
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({ value, label: getLabel(item, value) });
  });

  return options;
};

const resolveScopeSelection = (scope, options, normalizeValue) => {
  const availableValues = options.map((option) => option.value);
  // A nullable legacy scope means unrestricted. An explicit empty array means none.
  if (scope === null || scope === undefined) return availableValues;
  if (!Array.isArray(scope)) return [];

  const selected = new Set(scope.map(normalizeValue).filter(Boolean));
  return availableValues.filter((value) => selected.has(value));
};

const isUnrestrictedScope = (scope) => scope === null || scope === undefined;

const buildInitialForm = ({ category, branchOptions, categoryOptions, formatOptions, marginPercentDisplay, marginMode }) => {
  const isExisting = category !== null && category !== undefined;
  return {
    name: isExisting ? String(category?.name || '') : '',
    groupId: category?.groupId || '',
    marginPercent: marginReductionPercentToDisplay(category?.marginReductionPercent, marginPercentDisplay),
    description: isExisting ? String(category?.description || '') : '',
    isActive: isExisting ? category?.isActive !== false : false,
    applicableBranchIdsUnrestricted: isExisting && isUnrestrictedScope(category?.applicableBranchIds),
    applicableBranchIds: isExisting
      ? resolveScopeSelection(category?.applicableBranchIds, branchOptions, normalizeNumericId)
      : branchOptions.map((option) => option.value),
    applicableCategoryIdsUnrestricted: isExisting && isUnrestrictedScope(category?.applicableCategoryIds),
    applicableCategoryIds: isExisting
      ? resolveScopeSelection(category?.applicableCategoryIds, categoryOptions, normalizeNumericId)
      : categoryOptions.map((option) => option.value),
    applicableProductFormatCodesUnrestricted: isExisting
      ? isUnrestrictedScope(category?.applicableProductFormatCodes) : marginMode,
    applicableProductFormatCodes: isExisting
      ? resolveScopeSelection(category?.applicableProductFormatCodes, formatOptions, normalizeCode)
      : formatOptions.map((option) => option.value),
  };
};

const ScopeCheckboxGroup = ({
  legend,
  controlLabel,
  fieldName,
  options,
  selectedValues,
  onChange,
  disabled,
  emptyMessage,
}) => {
  const selected = new Set(selectedValues);
  const toggleValue = (value) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(options.map((option) => option.value).filter((optionValue) => next.has(optionValue)));
  };

  return (
    <fieldset className="rounded-xl border border-gray-200 bg-gray-50/60 p-4" disabled={disabled}>
      <legend className="px-1 text-sm font-semibold text-gray-900">{legend}</legend>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="mt-1 text-xs text-gray-500">
          {selected.size} de {options.length} seleccionadas
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange(options.map((option) => option.value))}
            disabled={disabled || !options.length || selected.size === options.length}
            aria-label={`Seleccionar todas las ${controlLabel}`}
            className="rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Seleccionar todas
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={disabled || selected.size === 0}
            aria-label={`Limpiar ${controlLabel}`}
            className="rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Limpiar
          </button>
        </div>
      </div>

      {options.length ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
            >
              <input
                type="checkbox"
                name={fieldName}
                value={option.value}
                checked={selected.has(option.value)}
                onChange={() => toggleValue(option.value)}
                disabled={disabled}
                className="h-4 w-4 accent-blue-600"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      ) : (
        <p className="mt-3 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-4 text-sm text-gray-500">
          {emptyMessage}
        </p>
      )}
    </fieldset>
  );
};

const ClientPricingCategoryModal = ({
  isOpen = false,
  initialData,
  category,
  branches = [],
  productCategories = [],
  productFormats = [],
  groups = [],
  pricingMode = 'discount',
  marginPercentDisplay = 'reduction',
  saving = false,
  loading = false,
  error = '',
  onClose,
  onSave,
}) => {
  const resolvedCategory = initialData ?? category ?? null;
  const isExisting = resolvedCategory !== null;
  const marginMode = pricingMode === 'margin_discount' || resolvedCategory?.marginReductionPercent != null;
  const branchOptions = useMemo(() => buildOptions(
    branches,
    (branch) => normalizeNumericId(branch.id),
    (branch, value) => branch.branchName || branch.name || `Sucursal ${value}`
  ), [branches]);
  const categoryOptions = useMemo(() => buildOptions(
    productCategories,
    (productCategory) => normalizeNumericId(productCategory.id),
    (productCategory, value) => productCategory.name || `Categoria ${value}`
  ), [productCategories]);
  const formatOptions = useMemo(() => buildOptions(
    productFormats,
    (format) => normalizeCode(format.code || format.clientType),
    (format, value) => format.name || value
  ), [productFormats]);
  const initialForm = useMemo(() => buildInitialForm({
    category: resolvedCategory,
    marginMode,
    marginPercentDisplay,
    branchOptions,
    categoryOptions,
    formatOptions,
  }), [branchOptions, categoryOptions, formatOptions, resolvedCategory, marginMode, marginPercentDisplay]);
  const [form, setForm] = useState(initialForm);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setForm(initialForm);
    setValidationError('');
  }, [initialForm, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, saving]);

  if (!isOpen) return null;

  const disabled = loading || saving;
  const handleSubmit = (event) => {
    event.preventDefault();
    if (disabled) return;
    const name = form.name.trim();
    if (!name) {
      setValidationError('El nombre de la categoria es obligatorio.');
      return;
    }

    const hasPercent = String(form.marginPercent).trim() !== '';
    if (marginMode && ((!isExisting || resolvedCategory?.marginReductionPercent != null) && !hasPercent)) {
      setValidationError('Ingresa el porcentaje general de margen.');
      return;
    }
    if (hasPercent && (!Number.isFinite(Number(form.marginPercent)) || Number(form.marginPercent) < 0 || Number(form.marginPercent) > 100)) {
      setValidationError('El porcentaje debe estar entre 0 y 100.');
      return;
    }
    setValidationError('');
    onSave?.({
      groupId: form.groupId ? Number(form.groupId) : null,
      ...(marginMode && hasPercent ? { marginReductionPercent: Number(marginDisplayPercentToReduction(form.marginPercent, marginPercentDisplay)) } : {}),
      name,
      description: form.description.trim() || null,
      isActive: Boolean(form.isActive),
      applicableBranchIds: form.applicableBranchIdsUnrestricted
        ? null
        : form.applicableBranchIds.map(Number),
      applicableCategoryIds: form.applicableCategoryIdsUnrestricted
        ? null
        : form.applicableCategoryIds.map(Number),
      applicableProductFormatCodes: form.applicableProductFormatCodesUnrestricted
        ? null
        : [...form.applicableProductFormatCodes],
    });
  };

  const requestClose = () => {
    if (!saving) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1500] flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-pricing-category-modal-title"
        aria-describedby="client-pricing-category-modal-description"
        aria-busy={disabled}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-gray-100 bg-white shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-100 bg-gray-50 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Descuentos por cliente</p>
            <h2 id="client-pricing-category-modal-title" className="mt-1 text-lg font-semibold text-gray-900">
              {isExisting ? 'Editar categoria de descuento' : 'Nueva categoria de descuento'}
            </h2>
            <p id="client-pricing-category-modal-description" className="mt-1 text-xs text-gray-500">
              Define sus datos y donde puede aplicarse. Las categorias nuevas quedan inactivas por defecto.
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={saving}
            aria-label="Cerrar modal de categoria de descuento"
            className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        <form className="space-y-6 p-6" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr),auto]">
            <div className="space-y-4">
              <div>
                <label htmlFor="client-pricing-category-name" className="text-xs font-semibold uppercase text-gray-600">
                  Nombre
                </label>
                <input
                  id="client-pricing-category-name"
                  type="text"
                  value={form.name}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, name: event.target.value }));
                    setValidationError('');
                  }}
                  disabled={disabled}
                  autoFocus
                  className="mt-2 w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label htmlFor="client-pricing-category-description" className="text-xs font-semibold uppercase text-gray-600">
                  Descripcion
                </label>
                <textarea
                  id="client-pricing-category-description"
                  rows={3}
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  disabled={disabled}
                  className="mt-2 w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                />
              </div>
            </div>

            <label className="flex h-fit min-w-[190px] items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 md:mt-6">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
                disabled={disabled}
                className="h-4 w-4 accent-blue-600"
              />
              <span>
                <span className="block font-semibold text-gray-900">Categoria activa</span>
                <span className="mt-0.5 block text-xs text-gray-500">Permite aplicar sus descuentos.</span>
              </span>
            </label>
          </div>

          <label className="block text-sm font-semibold text-gray-700">
            Grupo
            <select aria-label="Grupo de descuento" value={form.groupId} onChange={(event) => setForm((current) => ({ ...current, groupId: event.target.value }))} disabled={disabled} className="mt-2 w-full rounded-lg border border-gray-300 bg-white p-2.5">
              <option value="">Sin grupo</option>
              {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
            </select>
          </label>
          {marginMode && <section className="space-y-2 rounded-xl border border-blue-100 bg-blue-50 p-4">
            <label className="block text-sm font-semibold text-blue-950">
              {marginPercentDisplay === 'retained' ? 'Dejar el margen en' : 'Reducir el margen en'}
              <span className="mt-2 flex items-center gap-2"><input aria-label="Porcentaje general de margen" type="number" min="0" max="100" step="any" inputMode="decimal" value={form.marginPercent} onChange={(event) => setForm((current) => ({ ...current, marginPercent: event.target.value }))} disabled={disabled} className="w-32 rounded-lg border border-gray-300 bg-white p-2.5" />%</span>
            </label>
            <p className="text-xs text-blue-800">Un solo porcentaje para todos los productos de las categorías seleccionadas, incluidos los que se agreguen después. Se recalcula con el precio y costo actuales.</p>
            {isExisting && resolvedCategory?.marginReductionPercent == null && <p className="text-xs text-amber-800">Al ingresar y guardar un porcentaje general se reemplazarán todos los descuentos individuales de esta categoría. Déjalo vacío para conservarlos.</p>}
          </section>}
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.applicableProductFormatCodesUnrestricted} onChange={(event) => setForm((current) => ({ ...current, applicableProductFormatCodesUnrestricted: event.target.checked, ...(event.target.checked ? { applicableProductFormatCodes: formatOptions.map((option) => option.value) } : {}) }))} disabled={disabled} />Todos los formatos, incluidos los futuros</label>

          {loading && (
            <p role="status" className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
              Cargando opciones de aplicabilidad...
            </p>
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            <ScopeCheckboxGroup
              legend="Sucursales aplicables"
              controlLabel="sucursales"
              fieldName="applicableBranchIds"
              options={branchOptions}
              selectedValues={form.applicableBranchIds}
              onChange={(applicableBranchIds) => setForm((current) => ({
                ...current,
                applicableBranchIds,
                applicableBranchIdsUnrestricted: false,
              }))}
              disabled={disabled}
              emptyMessage="No hay sucursales disponibles."
            />
            <ScopeCheckboxGroup
              legend="Categorias de producto aplicables"
              controlLabel="categorias de producto"
              fieldName="applicableCategoryIds"
              options={categoryOptions}
              selectedValues={form.applicableCategoryIds}
              onChange={(applicableCategoryIds) => setForm((current) => ({
                ...current,
                applicableCategoryIds,
                applicableCategoryIdsUnrestricted: false,
              }))}
              disabled={disabled}
              emptyMessage="No hay categorias de producto disponibles."
            />
            <ScopeCheckboxGroup
              legend="Formatos aplicables"
              controlLabel="formatos"
              fieldName="applicableProductFormatCodes"
              options={formatOptions}
              selectedValues={form.applicableProductFormatCodes}
              onChange={(applicableProductFormatCodes) => setForm((current) => ({
                ...current,
                applicableProductFormatCodes,
                applicableProductFormatCodesUnrestricted: false,
              }))}
              disabled={disabled}
              emptyMessage="No hay formatos disponibles."
            />
          </div>

          {(validationError || error) && (
            <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {validationError || error}
            </p>
          )}

          <footer className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={requestClose}
              disabled={saving}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={disabled}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Guardando...' : isExisting ? 'Guardar cambios' : 'Crear categoria'}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};

export default ClientPricingCategoryModal;
